import { orderSidebarProjects } from "@app/components/assistant/conversation/utils";
import { useWelcomeTourGuide } from "@app/components/assistant/WelcomeTourGuideProvider";
import {
  type NavigationPanelSection,
  useDesktopNavigation,
} from "@app/components/navigation/DesktopNavigationContext";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { UserMenu } from "@app/components/UserMenu";
import { usePodConversationsSummary } from "@app/hooks/conversations";
import { LinkWrapper, useAppRouter, usePathParam } from "@app/lib/platform";
import { useChats } from "@app/lib/swr/chats";
import { useWorkspacePermissions } from "@app/lib/swr/permissions";
import { usePersonalTasks } from "@app/lib/swr/personal_tasks";
import {
  getChatsRoute,
  getHomeRoute,
  getLibraryRoute,
  getPeopleRoute,
  getPodRoute,
  getPodsRoute,
  getTasksRoute,
} from "@app/lib/utils/router";
import type {
  ConcreteResourceType,
  GrantVerb,
} from "@app/types/group_permissions";
import type { SubscriptionType } from "@app/types/plan";
import type { UserTypeWithWorkspaces, WorkspaceType } from "@app/types/user";
import { isManager } from "@app/types/user";
import {
  BookOpen01,
  CheckDone01,
  cn,
  Folder,
  Home01,
  Icon,
  MessageChatCircle,
  Users01,
} from "@dust-tt/sparkle";
import { motion, useReducedMotion } from "framer-motion";
import type { ComponentType } from "react";
import { useContext, useEffect, useMemo } from "react";

type RailItemId = "home" | NavigationPanelSection | "tasks" | "library";

interface RailItemProps {
  label: string;
  icon: ComponentType;
  isActive: boolean;
  href?: string;
  count?: number;
  onClick?: () => void;
  onNavigate?: () => void;
  isShown?: boolean;
}

function railItemClass(isActive: boolean) {
  return cn(
    "group/rail flex w-16 flex-col items-center gap-1 rounded-xl py-1 text-muted-foreground outline-none",
    "transition-colors duration-150 motion-reduce:transition-none",
    "hover:text-foreground",
    "focus-visible:ring-2 focus-visible:ring-highlight-300",
    isActive && "text-foreground"
  );
}

function RailItem({
  label,
  icon,
  isActive,
  href,
  count,
  onClick,
  onNavigate,
  isShown = false,
}: RailItemProps) {
  const reduceMotion = useReducedMotion();
  const hasCount = count !== undefined && count > 0;
  const content = (
    <>
      <span
        className={cn(
          "relative flex h-9 w-9 items-center justify-center rounded-xl",
          "transition-colors duration-150 motion-reduce:transition-none",
          !isActive && "group-hover/rail:bg-hover"
        )}
      >
        <span
          aria-hidden
          className={cn(
            "absolute -left-[18px] top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-highlight",
            "origin-left transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
            isShown ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"
          )}
        />
        {isActive && (
          <motion.span
            layoutId="memoair-rail-active"
            aria-hidden
            className="absolute inset-0 rounded-xl bg-selected"
            transition={
              reduceMotion
                ? { duration: 0 }
                : { type: "spring", stiffness: 500, damping: 38 }
            }
          />
        )}
        <Icon visual={icon} size="sm" aria-hidden className="relative" />
        {hasCount && (
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1",
              "bg-[#e5484d] text-[10px] font-semibold leading-none text-white",
              "ring-2 ring-app-background"
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </span>
      <span
        className={cn(
          "max-w-full truncate text-[11px] leading-tight",
          isActive || isShown ? "font-semibold" : "font-medium"
        )}
      >
        {label}
      </span>
      {hasCount && <span className="sr-only">, {count} waiting</span>}
    </>
  );

  return (
    // Clicks bubble here from both the link and the button, so the mobile
    // sheet can close without LinkWrapper exposing an onClick.
    <div onClickCapture={onNavigate}>
      {href ? (
        <LinkWrapper href={href} className={railItemClass(isActive)}>
          {content}
        </LinkWrapper>
      ) : (
        <button
          type="button"
          onClick={onClick}
          aria-pressed={isShown}
          className={cn(
            railItemClass(isActive),
            isShown && !isActive && "text-foreground"
          )}
        >
          {content}
        </button>
      )}
    </div>
  );
}

function getAdminSectionHref(
  owner: WorkspaceType,
  subscription: SubscriptionType,
  hasPermission: (
    verb: GrantVerb,
    resourceType: ConcreteResourceType
  ) => boolean
): string | null {
  if (subscription.plan.limits.canUseProduct) {
    return `/w/${owner.sId}/me`;
  }
  if (isManager(owner)) {
    return `/w/${owner.sId}/members`;
  }
  if (hasPermission("admin", "billing")) {
    return `/w/${owner.sId}/billing`;
  }
  if (hasPermission("admin", "security")) {
    return `/w/${owner.sId}/identity-and-provisioning`;
  }
  return null;
}

function getRouteSection(
  path: string,
  workspacePrefix: string
): NavigationPanelSection | null {
  if (path.startsWith(`${workspacePrefix}/pods`)) {
    return "projects";
  }
  if (path === `${workspacePrefix}/people`) {
    return "contacts";
  }
  if (
    path.startsWith(`${workspacePrefix}/people/conversations`) ||
    path.startsWith(`${workspacePrefix}/chats`)
  ) {
    return "dms";
  }
  return null;
}

export function useNavigationPanelSection(owner: WorkspaceType) {
  const router = useAppRouter();
  const { panelSection, setPanelSection } = useDesktopNavigation();
  const chatId = usePathParam("chatId");
  const { memoair: memoairChats } = useChats({ owner });
  const pathSection = getRouteSection(router.pathname, `/w/${owner.sId}`);
  const routeSection =
    pathSection === "dms" && memoairChats.some((chat) => chat.sId === chatId)
      ? "projects"
      : pathSection;

  // The route picks the section on navigation only; deriving it on every
  // render would pin the panel and swallow rail clicks on DM/project pages.
  useEffect(() => {
    if (routeSection) {
      setPanelSection(routeSection);
    }
  }, [routeSection, setPanelSection]);

  return { panelSection, setPanelSection };
}

interface NavigationRailProps {
  owner: WorkspaceType;
  subscription: SubscriptionType;
  user: UserTypeWithWorkspaces | null;
}

export function NavigationRail({
  owner,
  subscription,
  user,
}: NavigationRailProps) {
  const router = useAppRouter();
  const { setIsNavigationBarOpen, isNavigationBarOpen } =
    useDesktopNavigation();
  const { panelSection, setPanelSection } = useNavigationPanelSection(owner);
  const { setSidebarOpen } = useContext(SidebarContext);
  const { spaceMenuButtonRef } = useWelcomeTourGuide();
  const { hasPermission } = useWorkspacePermissions();
  // Proposals are the only task state the nav announces: they are the ones
  // waiting on a yes or a no. Same SWR key as Home, so mounting both is one
  // request.
  const { tasks } = usePersonalTasks({ owner });
  const proposedCount = useMemo(
    () =>
      tasks.filter(
        (task) =>
          task.agentSuggestionStatus === "pending" && task.status !== "done"
      ).length,
    [tasks]
  );

  const { summary: projectsSummary } = usePodConversationsSummary({
    workspaceId: owner.sId,
  });

  const workspacePrefix = `/w/${owner.sId}`;
  const path = router.pathname;
  const pageItem: RailItemId | null =
    path === `${workspacePrefix}/home` || path === workspacePrefix
      ? "home"
      : path === `${workspacePrefix}/tasks`
        ? "tasks"
        : path.startsWith(`${workspacePrefix}/library`)
          ? "library"
          : null;
  // One highlight at a time: a page destination wins, otherwise the rail
  // points at the list the panel is showing.
  const activeItem = isNavigationBarOpen ? panelSection : pageItem;

  // The list panel belongs to DMs, Projects and Contacts only: page destinations
  // close it, conversation-level routes open it.
  useEffect(() => {
    setIsNavigationBarOpen(pageItem === null);
  }, [pageItem, setIsNavigationBarOpen]);

  const closeSheet = () => setSidebarOpen(false);

  const toggleSection = (section: NavigationPanelSection) => {
    if (isNavigationBarOpen && panelSection === section) {
      setIsNavigationBarOpen(false);
      return;
    }
    setPanelSection(section);
    setIsNavigationBarOpen(true);
    setSidebarOpen(true);
  };

  return (
    <nav
      aria-label="Primary"
      className="flex h-full w-[72px] shrink-0 flex-col items-center gap-3 py-3"
    >
      <LinkWrapper
        href={getHomeRoute(owner.sId)}
        className="flex h-10 w-10 items-center justify-center rounded-xl"
      >
        <img
          src="/static/brand/memoair-mark.png"
          alt="MemoAir home"
          className="h-8 w-8 object-contain"
        />
      </LinkWrapper>
      <div className="my-auto flex flex-col items-center gap-3 pb-16">
        <div className="flex flex-col items-center gap-0.5">
          <RailItem
            label="Home"
            icon={Home01}
            href={getHomeRoute(owner.sId)}
            onNavigate={closeSheet}
            isActive={activeItem === "home"}
          />
          <RailItem
            label="DMs"
            icon={MessageChatCircle}
            isActive={activeItem === "dms"}
            isShown={isNavigationBarOpen && panelSection === "dms"}
            onClick={() => {
              const isOpening = !(
                isNavigationBarOpen && panelSection === "dms"
              );
              toggleSection("dms");
              if (isOpening && !path.startsWith(`${workspacePrefix}/chats`)) {
                void router.push(getChatsRoute(owner.sId));
              }
            }}
          />
          <RailItem
            label="Projects"
            icon={Folder}
            isActive={activeItem === "projects"}
            isShown={isNavigationBarOpen && panelSection === "projects"}
            onClick={() => {
              const isOpening = !(
                isNavigationBarOpen && panelSection === "projects"
              );
              toggleSection("projects");
              if (isOpening && !path.startsWith(`${workspacePrefix}/pods`)) {
                const ordered = orderSidebarProjects(projectsSummary, "");
                const topProject =
                  ordered.find(({ space }) => !space.systemKind) ?? ordered[0];
                void router.push(
                  topProject
                    ? getPodRoute(owner.sId, topProject.space.sId)
                    : getPodsRoute(owner.sId)
                );
              }
            }}
          />
          <RailItem
            label="Tasks"
            icon={CheckDone01}
            href={getTasksRoute(owner.sId)}
            onNavigate={closeSheet}
            count={proposedCount}
            isActive={activeItem === "tasks"}
          />
          <RailItem
            label="Contacts"
            icon={Users01}
            isActive={activeItem === "contacts"}
            isShown={isNavigationBarOpen && panelSection === "contacts"}
            onClick={() => {
              const isOpening = !(
                isNavigationBarOpen && panelSection === "contacts"
              );
              toggleSection("contacts");
              if (isOpening && path !== `${workspacePrefix}/people`) {
                void router.push(getPeopleRoute(owner.sId));
              }
            }}
          />
          <RailItem
            label="Library"
            icon={BookOpen01}
            href={getLibraryRoute(owner.sId)}
            onNavigate={closeSheet}
            isActive={activeItem === "library"}
          />
        </div>
        {user && (
          <>
            <hr
              aria-hidden
              className="w-8 border-0 border-t border-dashed border-stone-300 dark:border-stone-700"
            />
            <UserMenu
              user={user}
              owner={owner}
              subscription={subscription}
              adminHref={getAdminSectionHref(
                owner,
                subscription,
                hasPermission
              )}
              adminButtonRef={spaceMenuButtonRef}
              variant="compact"
            />
          </>
        )}
      </div>
    </nav>
  );
}
